<template>
  <DialogWrapper
    class="invite-rating-dialog"
    cancelBtnLabelI18nKey="common.btn.prev"
    :saveBtnLabelI18nKey="saveBtnLabelI18nKey"
    :loading="loading"
    :show.sync="show"
    :hideCancelBtn="hideCancelBtn"
    :closeOnCancel="false"
    :closeOnClickModal="false"
    @closed="handleClosedDialog"
  >
    <div class="hm-rate-form">
      <Step :stepList="stepList" :activeIndex="activeIndex"></Step>
      <StepContent class="step-content" :stepList="stepList" :activeIndex="activeIndex">
        <template #hmSelector>
          <HmSelector
            class="hm-selector"
            ref="hmSelector"
            :applicationList="applicationList"
            @changeHmList="handleHmListChange"
          >
          </HmSelector>
        </template>

        <template #addComments="{ step }">
          <div class="comments">
            <div class="message">{{ step.contentLabel }}</div>
            <el-input
              v-model="hmComment"
              type="textarea"
              class="content"
              :autosize="{ minRows: 5, maxRows: 8}"
              :placeholder="$t(`${i18nPrefix}.placeholder.commentsPlaceholder`)"
              >
            </el-input>
          </div>
        </template>

        <template #questionSelector="{ step }">
          <div class="questionnaire-step">
            <div class="message">{{ step.contentLabel }}</div>
            <div class="questionnaire-actions">
              <el-button class="questionnaire-action-btn" @click="handleOpenQuestionnaireSelector">{{ $t(`${i18nPrefix}.label.selectQuestionnaire`) }}</el-button>
              <el-button class="questionnaire-action-btn" @click="handleAddQuestionnaire">{{ $t(`${i18nPrefix}.label.addQuestionnaire`) }}</el-button>
            </div>
            <div v-if="selectedQuestionnaire" class="selected-questionnaire-preview">
              <QuestionnairePreview
                :questionnaire="selectedQuestionnaire"
                :items="selectedQuestionnaireItems"
              />
            </div>
          </div>
        </template>

        <template #emailEditor="{ step }">
          <div class="invitation">
            <div class="invitation">
              <div class="message">{{ step.contentLabel }}</div>
              <div class="select-language">
                <span class="language-title">{{ $t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`) }}</span>
                <el-select v-model="sendWithLanguage" class="language-select" :placeholder="$t(`${i18nPrefix}.placeholder.interviewEmailTemplateLanguage`)" :disabled="disabledHmOperateBtn" @change="handleEmailLanguageChange">
                  <el-option
                    v-for="(item, index) in languageList"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </div>
              <div class="invitation-subject">
                <div class="subject-label">{{ $t(`${i18nPrefix}.label.invitationSubject`) }}</div>
                <el-input v-model="invitationSubject"></el-input>
              </div>
                <TinymceEditor
                  v-if="showEmailTemplate"
                  class="invitation-content"
                  v-model="invitationContent"
                  showTemplateField
                  :showGenerateAISummaryButton="hasAISummaryPermission"
                  :showGenerateAIMatchButton="hasAIResumeMatchPermission"
                  :applicationList="applicationList"
                  :disabled="false"
                  :emailLanguage="sendWithLanguage"
                  :aiTemplateField="AI_SUMMARY_TEMPLATE_FIELD"
                  :themeColor="emailThemeColor"
                  @generateAISummary="handleGenerateAISummary"
                  @generateAIMatch="handleGenerateAIMatch"
                  @enableOperateBtn="handleEnableOperateBtn"
                >
                </TinymceEditor>
            </div>
          </div>
        </template>
      </StepContent>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button v-show="!hideCancelBtn" class="footer-btn cancel-btn" :disabled="disabledHmOperateBtn" @click="handleCancel">{{ $t('common.btn.prev') }}</el-button>
        <el-button v-if="activeIndex === stepList.length - 1" class="footer-btn save-btn" :disabled="disabledHmOperateBtn" @click="handlePreviewInviteEmail">{{ $t(`${i18nPrefix}.btn.previewEmail`) }}</el-button>
        <el-button class="footer-btn save-btn" :disabled="disabledHmOperateBtn" @click="handleSave">{{ $t(saveBtnLabelI18nKey) }}</el-button>
      </div>
    </template>
    <DialogWrapper
      class="preview-invite-rate-email-dialog"
      appendToBody
      hideFooter
      fullscreen
      save-btn-label-i18n-key="common.btn.send"
      :show.sync="showPreviewInviteEmailDialog"
      :title="$t(`${i18nPrefix}.dialogTitle.previewInviteRateEmailDialogTitle`)"
      @save="handleSave"
    >
      <div class="operation-btn">
        <el-button class="footer-btn cancel-btn" @click="showPreviewInviteEmailDialog = false">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="footer-btn save-btn" @click="handleSave">{{ $t('common.btn.send') }}</el-button>
      </div>
      <div v-html="previewInviteEmailContent" v-loading="loadingInviteEmail" class="preview-content"></div>
    </DialogWrapper>
    <QuestionnaireSelectorDialog
      ref="questionnaireSelectorDialog"
      :show.sync="showQuestionnaireSelector"
      :selectedQuestionnaire="selectedQuestionnaire"
      @confirm="handleConfirmQuestionnaire"
    />
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { getI18nPropertyValue } from '@/i18n'
import { mapActions } from 'vuex'
import Step from '@/components/step/stepMenu.vue'
import DialogWrapper from '../../../../components/dialog/dialogWrapper.vue'
import HmSelector from '../hmSelector.vue'
import StepContent from '../stepContent.vue'
import TinymceEditor from '../../application/components/tinymceEditor.vue'
import QuestionnairePreview from './components/questionnairePreview.vue'
import QuestionnaireSelectorDialog from './components/questionnaireSelectorDialog.vue'
import { AsyncTask } from '@/utils/common'

const NAME_LIST = {
  HM_SELECTOR: 'HM_SELECTOR',
  HM_COMMENT: 'HM_COMMENT',
  QUESTION_SELECTOR: 'QUESTION_SELECTOR',
  EMAIL_EDITOR: 'EMAIL_EDITOR'
}

const INVITE_HM_RATE_EMAIL_TEMPLATE_TYPE = 'Invite HM Rate'

// 在邮件模板中使用的占位符，AI summary 将在这个位置插入
const AI_SUMMARY_TEMPLATE_FIELD = '$' + '{candidate_info_table}'

export default {
  components: {
    Step,
    DialogWrapper,
    HmSelector,
    StepContent,
    TinymceEditor,
    QuestionnairePreview,
    QuestionnaireSelectorDialog
  },
  props: {
    applicationList: {
      required: true
    },
    isAllApplication: {
      required: false
    },
    searchParams: {
      required: false
    }
  },
  data () {
    return {
      AI_SUMMARY_TEMPLATE_FIELD,
      show: false,
      i18nPrefix: 'admin.application.applicantList',
      loading: false,
      activeIndex: 0,
      hmComment: null,
      selectHmList: [],
      selectedQuestionnaire: null,
      showQuestionnaireSelector: false,
      NAME_LIST,
      invitationSubjectEn: null,
      invitationSubjectCn: null,
      invitationContentEn: null,
      invitationContentCn: null,
      emailThemeColor: '#0096cc',
      sendWithLanguage: 'zh-cn',
      showPreviewInviteEmailDialog: false,
      loadingInviteEmail: false,
      previewInviteEmailContent: null,
      showEmailTemplate: false,
      disabledHmOperateBtn: false
    }
  },
  computed: {
    languageList () {
      const list = [
        {
          label: this.$t('common.label.english'),
          value: 'en'
        },
        {
          label: this.$t('common.label.chinese'),
          value: 'zh-cn'
        }
      ]
      return list
    },
    invitationSubject: {
      get () {
        return this.sendWithLanguage === 'en' ? this.invitationSubjectEn : this.invitationSubjectCn
      },
      set (value) {
        if (this.sendWithLanguage === 'en') {
          this.invitationSubjectEn = value
        } else {
          this.invitationSubjectCn = value
        }
      }
    },
    invitationContent: {
      get () {
        return this.sendWithLanguage === 'en' ? this.invitationContentEn : this.invitationContentCn
      },
      set (value) {
        if (this.sendWithLanguage === 'en') {
          this.invitationContentEn = value
        } else {
          this.invitationContentCn = value
        }
      }
    },
    stepList () {
      const step = [
        {
          name: NAME_LIST.HM_SELECTOR,
          title: this.$t(`${this.i18nPrefix}.title.hiringManage`),
          contentSlot: 'hmSelector'
        },
        {
          hidden: this.applicationList.length > 1,
          name: NAME_LIST.HM_COMMENT,
          title: this.$t(`${this.i18nPrefix}.title.addComments`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.hiringComments`),
          contentSlot: 'addComments'
        },
        {
          name: NAME_LIST.QUESTION_SELECTOR,
          title: this.$t(`${this.i18nPrefix}.title.selectQuestionnaire`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.ratingQuestionnaire`),
          contentSlot: 'questionSelector'
        },
        {
          name: NAME_LIST.EMAIL_EDITOR,
          title: this.$t(`${this.i18nPrefix}.title.sendInvitation`),
          contentLabel: this.$t(`${this.i18nPrefix}.label.invitation`),
          contentSlot: 'emailEditor'
        }
      ]
      return _.filter(step, item => !item.hidden)
    },
    hideCancelBtn () {
      return this.activeIndex === 0
    },
    jobsName () {
      return _.chain(this.applicationList).map(application => getI18nPropertyValue(application, 'job.cn_title', 'job.title')).uniq().join(',').value()
    },
    saveBtnLabelI18nKey () {
      const isLastStep = this.activeIndex === this.stepList.length - 1
      return isLastStep ? 'common.btn.send' : 'common.btn.next'
    },
    hasAISummaryPermission () {
      return this.hasAnyPermission('PREMIUM_AI_SUMMARY')
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    selectedQuestionnaireItems () {
      return _.get(this.selectedQuestionnaire, 'items') || []
    }
  },
  methods: {
    ...mapActions('hm', ['sendInviteHm', 'previewInviteRateEmail']),
    ...mapActions('notificationTemplate', ['getCompanyInviteHmRateEmailTemplate']),
    ...mapActions('ai', ['createAISummary', 'getAISummaryInfoByTaskId', 'createAIResumeMatch', 'getAIMatchInfoByTaskId']),
    ...mapActions('application', ['getApplicationAISummary', 'getApplicationAIMatchInfo']),
    async loadApplicationAISummary () {
      const params = {
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
      }
      const resp = await this.getApplicationAISummary(params)
      if (resp) {
        _.forEach(resp, (aiSummaryInfo, candidateId) => {
          const application = _.find(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          const index = _.findIndex(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          application.candidate.ai_summary_info = aiSummaryInfo
          this.applicationList.splice(index, 1, application)
        })
      }
    },
    async loadApplicationAIMatchInfo () {
      const params = {
        job_id: this.applicationList[0].job.id,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
      }
      const resp = await this.getApplicationAIMatchInfo(params)
      if (resp) {
        _.forEach(resp, (aiMatchInfo, candidateId) => {
          const application = _.find(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          const index = _.findIndex(this.applicationList, item => item.candidate.id === _.parseInt(candidateId))
          application.candidate.ai_match_info = aiMatchInfo
          this.applicationList.splice(index, 1, application)
        })
      }
    },
    async handlePreviewInviteEmail () {
      if (!this.invitationContent) {
        return
      }
      this.previewInviteEmailContent = null
      const params = {
        job_id: this.applicationList[0].job.id,
        template_content: this.invitationContent,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id),
        hm_info_list: this.selectHmList,
        language_code: this.sendWithLanguage
      }
      this.showPreviewInviteEmailDialog = true
      this.loadingInviteEmail = true
      try {
        const resp = await this.previewInviteRateEmail(params)
        if (resp) {
          this.previewInviteEmailContent = resp
        }
      } finally {
        this.loadingInviteEmail = false
      }
    },
    async loadEmailTemplate () {
      this.showEmailTemplate = false
      this.loading = true
      try {
        const resp = await this.getCompanyInviteHmRateEmailTemplate({
          email_template_type: INVITE_HM_RATE_EMAIL_TEMPLATE_TYPE,
          job_id: this.applicationList[0].job.id
        })
        if (resp) {
          this.$nextTick(() => {
            this.invitationSubjectEn = resp.subject_en
            this.invitationSubjectCn = resp.subject_cn
            this.invitationContentEn = resp.content_en
            this.invitationContentCn = resp.content_cn
            this.emailThemeColor = resp.theme_color || '#0096cc'
          })
        }
      } finally {
        this.showEmailTemplate = true
        this.loading = false
      }
    },
    async showDialog () {
      this.show = true
      this.loading = true
      await this.$nextTick()
      this.activeIndex = 0
      this.selectHmList = []
      this.selectedQuestionnaire = null
      this.hmComment = null
      this.emailError = null
      this.firstNameError = null
      this.$refs.hmSelector.refreshForm()
      await this.loadDefaultQuestionnaire()
      this.loading = false
    },
    async loadDefaultQuestionnaire () {
      const defaultQuestionnaireId = _.get(this.applicationList, '[0].job.default_rating_questionnaire_id')
      if (!defaultQuestionnaireId) {
        return
      }
      try {
        this.selectedQuestionnaire = await this.$refs.questionnaireSelectorDialog.loadQuestionnaireById(defaultQuestionnaireId)
      } catch (e) {
        this.selectedQuestionnaire = null
      }
    },
    handleOpenQuestionnaireSelector () {
      this.showQuestionnaireSelector = true
    },
    handleAddQuestionnaire () {
      const route = this.$router.resolve({ path: '/v-admin/questionnaire-management/questionnaire/add' })
      window.open(route.href, '_blank')
    },
    handleConfirmQuestionnaire (questionnaire) {
      this.selectedQuestionnaire = questionnaire
    },
    handleBackClick () {
      if (this.activeIndex > 0) {
        this.activeIndex = this.activeIndex - 1
      }
    },
    async handleNextClick () {
      if (this.getCurrentStepIndex(NAME_LIST.HM_SELECTOR) === this.activeIndex) {
        const isValid = this.$refs.hmSelector.handleNextClick()
        if (!isValid) {
          return
        }
      }

      if (this.activeIndex < this.stepList.length - 1) {
        this.activeIndex = this.activeIndex + 1
      }
      if (this.activeIndex === this.stepList.length - 1) {
        const promiseList = []
        if (this.hasAISummaryPermission) {
          promiseList.push(this.loadApplicationAISummary())
        }
        if (this.hasAIResumeMatchPermission) {
          promiseList.push(this.loadApplicationAIMatchInfo())
        }
        promiseList.push(this.loadEmailTemplate())
        await Promise.all(promiseList)
        this.resetTinymceDialogStyle()
      }
    },
    resetTinymceDialogStyle () {
      // 由于插入 AI Summary 内容导致 tinymce 渲染在 body 上要等待 AI Summary 内容插入
      setTimeout(() => {
        const tinymceDialogs = document.querySelectorAll('.tox-tinymce-aux')
        if (tinymceDialogs && tinymceDialogs.length > 0) {
          _.forEach(tinymceDialogs, dialog => {
            dialog.style.zIndex = '99999999'
          })
        }
      }, 500)
    },
    async handleSendClick () {
      if (this.loading) {
        return
      }
      this.loading = true
      const params = {
        search_params: this.searchParams,
        is_all_application: this.isAllApplication,
        hire_managers: this.selectHmList,
        candidates: _.map(this.applicationList, item => item.candidate.id),
        job_id: this.applicationList[0].job.id, // Only the same job candidates can be invited
        comment: this.hmComment,
        email_template: {
          subject: this.invitationSubject,
          content: this.invitationContent,
          language_code: this.sendWithLanguage
        }
      }
      if (this.selectedQuestionnaire) {
        params.questionnaire_id = this.selectedQuestionnaire.id
        params.questions = _.map(this.selectedQuestionnaireItems, item => item.questionId)
      }
      try {
        const resp = await this.sendInviteHm(params)
        if (resp) {
          const success = resp.success
          this.$message({
            message: this.$t(`common.message.${success ? 'inviteSuccess' : 'inviteFailed'}`),
            type: success ? 'success' : 'error'
          })
          if (success) {
            this.show = false
            this.$emit('sent', resp)
          }
        }
      } finally {
        this.loading = false
      }
    },
    handleHmListChange (hmList) {
      this.selectHmList = hmList
    },
    handleCancel () {
      this.activeIndex = this.activeIndex - 1
    },
    handleClosedDialog () {
      this.showEmailTemplate = false
      this.handleEnableOperateBtn()
    },
    handleSave () {
      const isLastStep = this.activeIndex === this.stepList.length - 1
      if (isLastStep) {
        this.showPreviewInviteEmailDialog = false
        this.handleSendClick()
      } else {
        this.handleNextClick()
      }
    },
    getCurrentStepIndex (name) {
      return _.findIndex(this.stepList, item => item.name === name)
    },
    handleEmailLanguageChange () {
      this.showEmailTemplate = false
      this.$nextTick(() => {
        this.showEmailTemplate = true
        this.resetTinymceDialogStyle()
      })
    },
    handleGenerateAISummary (candidateId, jobId, languageCode, callback) {
      const application = _.find(this.applicationList, item => item.candidate.id === candidateId)
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiSummary = ''
      this.disabledHmOperateBtn = true
      aiTask.task = new AsyncTask(() => this.createAISummary({
        candidate_id: application.candidate.id,
        job_id: jobId,
        language_code: languageCode
      }), (taskId) => this.getAISummaryInfoByTaskId(taskId), (taskId, resp) => {
        let aiSummaryInfo
        if (resp) {
          aiSummaryInfo = resp.content
          aiSummary = languageCode === 'en' ? aiSummaryInfo.ai_summary_en : aiSummaryInfo.ai_summary_cn
          aiTask.isFinished = true
        }
        const content = {
          candidate_id: application.candidate.id,
          job_id: application.job.id,
          candidate_name: application.candidate.name,
          summary: aiSummary
        }

        if (aiSummaryInfo) {
          application.candidate.ai_summary_info = aiSummaryInfo
        }
        callback(content)
      })
    },
    handleGenerateAIMatch (candidateId, jobId, languageCode, callback) {
      const application = _.find(this.applicationList, item => item.candidate.id === candidateId)
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiMatchInfo = {}
      this.disabledHmOperateBtn = true
      aiTask.task = new AsyncTask(() => this.createAIResumeMatch({
        candidate_ids: [application.candidate.id],
        job_id: jobId,
        language_code: languageCode,
        await_result: true
      }), (taskId) => this.getAIMatchInfoByTaskId(taskId), (taskId, resp) => {
        if (resp) {
          aiTask.isFinished = true
          const result = resp.result
          const key = `${jobId}_${application.candidate.id}`
          aiMatchInfo = _.get(result, key)
          application.candidate.ai_match_info = aiMatchInfo
        }

        callback(aiMatchInfo)
      })
    },
    handleEnableOperateBtn () {
      this.disabledHmOperateBtn = false
    }
  }
}
</script>

<style lang="scss" scoped>
.invite-rating-dialog {
  ::v-deep .el-dialog {
    width: 60%;
  }
  .hm-rate-form {
    word-break: initial;

    .step-content {
      margin-top: 25px;

      .comments {
        .content {
          margin-top: 15px;
        }
      }

      .questionnaire-step {
        .questionnaire-actions {
          display: flex;
          margin-top: 16px;

          .questionnaire-action-btn {
            flex: 1;
            height: 39px;
            margin-left: 10px;
            border-radius: 4px;
            border-color: #c0c4cc;
            background: #0096cc;
            color: #ffffff;

            &:first-child {
              margin-left: 0;
            }
          }
        }

        .selected-questionnaire-preview {
          margin-top: 16px;
        }
      }

      .no-open-question {
        text-align: center;
        padding: 12px 0px;
        border: 1px solid #0296cc;
        color: #0296cc;
        margin-bottom: 15px;
        display: block;
        border-radius: 2px;
        margin-top: 40px;
      }

      .question-table {
        margin-top: 16px;
        max-height: 200px;
        overflow: auto;

        .question-item {
          display: flex;
          align-items: center;
          margin-left: 5px;

          .label {
            .question-order {
              display: inline-block;
              color: #0096cc;
              font-weight: bold;
              font-size: 20px;
              vertical-align: middle;
              padding: 2px 0 15px;

              .sloping-side {
                height: 0px;
                border-bottom: 1px solid #0096cc;
                width: 24px;
                transform: rotate(110deg);
                margin-top: -8px;
                margin-left: 1px;
              }
            }

            .question-type {
              display: inline-block;
              margin-left: 5px;
              vertical-align: baseline;

              .type-icon {
                display: inline-block;
                width: 16px;
                height: 16px;
                background-size: cover;
                background-repeat: no-repeat;
                margin-top: 10px;

                &.attachment {
                  background-image: url($baseDomain + 'static/image/sea/portfolio_icon.png');
                }

                &.video {
                  background-image: url($baseDomain + 'static/image/sea/video_icon_small.png');
                }

                &.single_select,
                &.dropdown {
                  background-image: url($baseDomain + 'static/image/sea/radio_icon.png');
                }

                &.text {
                  background-image: url($baseDomain + 'static/image/sea/text_icon.png');
                }

                &.multiple_select,
                &.checkbox {
                  background-image: url($baseDomain + 'static/image/sea/checkbox_icon.png');
                }

                &.icondate {
                  color: #0088cc;
                  font-size: 16px;
                  transform: translateX(-1px);
                }
              }
            }
          }

          .content {
            flex: 1;
            margin-left: 10px;
            padding: 16px 20px 0 20px;
            background-color: #f7f7f7;
            position: relative;

            .question-item-label {
              padding-bottom: 16px;
              border-bottom: 1px solid #dfdfdf;
              line-height: 20px;
            }

            .question-operation {
              transform: translateY(-50%);
              position: absolute;
              top: 50%;
              right: 20px;
            }

            .question-content {
              margin-right: 110px;
              flex: 1;
              font-weight: 400;
              font-size: 14px;
              color: #303133;
            }
          }

          &:last-child {
            .content {
              .question-item-label {
                border-bottom-color: transparent;
              }
            }
          }
        }
      }

      .invitation {
        .select-language {
          display: inline-block;
          margin-top: 10px;

          .language-title {
            display: inline-block;
            margin-right: 8px;
          }

          .language-select {
            width: 50%;
          }
        }

        .invitation-subject {
          display: flex;
          align-items: center;
          margin: 16px 0px;

          .subject-label {
            width: 55px;
            font-size: 18px;
            font-weight: 400;
            color: #606266;
          }

          ::v-deep {
            .el-input {
              flex: 1;
              margin-left: 23px;
              width: auto;
            }
          }
        }

        .invitation-content{
          padding-left: 78px
        }
      }
    }

    .message {
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .dialog-footer {
    .footer-btn  {
      border-radius: 2px;
      padding: 10px 30px;
    }

    .save-btn {
      background: #0096cc;
      border-color: #0096cc;
      color: #ffffff;
    }

    .cancel-btn {
      background: #ffffff;
      color: #606266;
    }
  }
}

.preview-invite-rate-email-dialog {
  overflow: hidden;

  ::v-deep {
    .el-dialog {
      margin-top: 0;
      height: 100%;
      overflow: hidden;

      .el-dialog__body {
        height: 94%;
        overflow: hidden;
        word-break: break-word;
        white-space: normal;
      }
    }
  }

  .operation-btn {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .footer-btn  {
      border-radius: 2px;
      padding: 10px 30px;
    }

    .save-btn {
      background: #0096cc;
      border-color: #0096cc;
      color: #ffffff;
    }

    .cancel-btn {
      background: #ffffff;
      color: #606266;
    }

  }
  .preview-content {
    margin-top: 10px;
    min-height: 500px;
    overflow-y: auto;
    max-height: 90%;
    ::v-deep {
      #custom-tag-candidate_ai_summary {
        .candidate-ai-summary {
          .candidate-summary-item {
            .name {
              cursor: not-allowed !important;
              a {
                cursor: not-allowed;
                pointer-events: none;
              }
            }
            .rate-btn {
              cursor: not-allowed !important;
              a {
                cursor: not-allowed;
                pointer-events: none;
              }
            }
          }
        }
        .view-dashboard-btn {
          cursor: not-allowed !important;
          a {
            cursor: not-allowed;
            pointer-events: none;
          }
        }
      }
    }
  }
}
</style>
